// src/admin/pages/CourseDetail.jsx

import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { adminService } from "../../services/adminService";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";

const CourseDetail = () => {
  const { courseId } = useParams();

  const [course, setCourse] = useState(null);
  const [learners, setLearners] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [aps, setAps] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [cohorts, setCohorts] = useState([]);
  const [cohortMap, setCohortMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [filterCoach, setFilterCoach] = useState("all");
  const [filterAp, setFilterAp] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  const [selectedUser, setSelectedUser] = useState("");

  /* ===========================
     LOAD COURSE DETAILS
  =========================== */
  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = await adminService.getCourseDetail(courseId);

      setCourse(data.course);
      setLearners(data.learners || []);
      setCoaches(data.coaches || []);
      setAps(data.accountability_partners || []);
      setAllUsers(data.all_users || []);
    } catch (err) {
      console.error("Failed to load course detail", err);
      setError("Failed to load course details");
      toast.error("Failed to load course details.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  /* ===========================
     LOAD COHORTS
  =========================== */
  const loadCohorts = useCallback(async () => {
    try {
      const all = await adminService.getCohorts();
      const filtered = all.filter(
        (c) => String(c.course_id) === String(courseId)
      );
      setCohorts(filtered);

      // Build learner → cohort mapping
      let map = {};
      for (const cohort of filtered) {
        const users = await adminService.getCohortUsers(cohort.id);
        users.forEach((u) => (map[u.user_id] = cohort.id));
      }
      setCohortMap(map);
    } catch (err) {
      console.error("Failed to load cohorts", err);
      toast.error("Failed to load cohorts.");
    }
  }, [courseId]);

  useEffect(() => {
    loadDetail();
    loadCohorts();
  }, [loadDetail, loadCohorts]);

  /* ===========================
     ENROLL USER INTO COURSE
  =========================== */
  const handleEnroll = async (userId) => {
    setSaving(true);
    try {
      await adminService.enrollLearner(courseId, userId);
      await loadDetail();
      toast.success("User enrolled successfully.");
      setSelectedUser(""); // reset after success
    } catch (err) {
      toast.error("Failed to enroll user.");
    } finally {
      setSaving(false);
    }
  };

  /* ===========================
     ASSIGN COHORT
  =========================== */
  const handleAssignCohort = async (learnerId, cohortId) => {
    setSaving(true);
    try {
      const existing = cohortMap[learnerId];

      if (existing) {
        await adminService.removeFromCohort(existing, learnerId);
      }

      if (cohortId) {
        await adminService.enrollIntoCohort(cohortId, learnerId);
      }

      await loadCohorts();
      toast.success("Cohort updated.");
    } catch {
      toast.error("Failed to update cohort.");
    } finally {
      setSaving(false);
    }
  };

  /* ===========================
     SEARCH + FILTER + SORT
  =========================== */
  const filteredLearners = useMemo(() => {
    let list = [...learners];
    const q = search.toLowerCase();

    if (q) {
      list = list.filter(
        (l) =>
          l.first_name.toLowerCase().includes(q) ||
          l.last_name.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q)
      );
    }

    if (filterCoach === "has") list = list.filter((l) => l.coach_id);
    else if (filterCoach === "none") list = list.filter((l) => !l.coach_id);

    if (filterAp === "has") list = list.filter((l) => l.ap_id);
    else if (filterAp === "none") list = list.filter((l) => !l.ap_id);

    if (sortBy === "name") {
      list.sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`
        )
      );
    } else {
      list.sort((a, b) => a.email.localeCompare(b.email));
    }

    return list;
  }, [learners, search, filterCoach, filterAp, sortBy]);

  const enrollableUsers = useMemo(() => {
    const learnerIds = new Set(learners.map((l) => l.user_id));
    return allUsers.filter((u) => !learnerIds.has(u.user_id));
  }, [allUsers, learners]);

  /* ===========================
     RENDER
  =========================== */
  if (loading && !course) {
    return (
      <div className="p-6 flex items-center gap-3 text-[#54B0AF]">
        <Loader2 size={22} className="animate-spin" />
        Loading course...
      </div>
    );
  }

  if (!course) {
    return <div className="p-6 text-red-600">Course not found.</div>;
  }

  return (
    <div className="space-y-10">
      {/* Header */}
      <div>
        <Link
          to="/admin/courses"
          className="text-sm text-[#54B0AF] hover:underline"
        >
          ← Back to Courses
        </Link>

        <h1 className="text-display mt-1">{course.title}</h1>
        <p className="text-muted mt-1">
          Slug: <span className="font-mono">{course.slug}</span>
        </p>
      </div>

      {/* Bulk Upload */}
      <Link
        to={`/admin/courses/${courseId}/bulk-upload`}
        className="inline-block px-4 py-2 bg-[#54B0AF] text-white rounded-md shadow hover:opacity-90 transition text-sm"
      >
        Bulk Upload Learners
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Learners" value={learners.length} />
        <StatCard label="Coaches" value={coaches.length} />
        <StatCard label="APs" value={aps.length} />
        <StatCard label="Cohorts" value={cohorts.length} />
      </div>

      {/* Description */}
      {course.description && (
        <div className="bg-white stakeholder-summary-card rounded-lg p-6 shadow">
          <h2 className="text-title mb-2">Description</h2>
          <p className="text-body">{course.description}</p>
        </div>
      )}

      {error && (
        <div className="text-red-600 text-sm bg-red-50 border p-2 rounded">
          {error}
        </div>
      )}
      {saving && <div className="text-xs text-muted">Saving changes...</div>}

      {/* Enroll User */}
      <div className="bg-white stakeholder-summary-card rounded-lg p-6 shadow flex items-center gap-4">
        <div>
          <h2 className="text-title">Enroll Learner</h2>
          <p className="text-muted text-xs">Add an existing user</p>
        </div>

        <select
          className="border rounded-md px-3 py-2 stakeholder-dropdown text-sm"
          value={selectedUser}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedUser(id);
            if (id) handleEnroll(id);
          }}
          disabled={saving || enrollableUsers.length === 0}
        >
          <option value="">
            {enrollableUsers.length === 0
              ? "No more users"
              : "Select user to enroll"}
          </option>

          {enrollableUsers.map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {`${u.first_name} ${u.last_name}`.trim() || u.email} ({u.email})
            </option>
          ))}
        </select>
      </div>

      {/* Filters */}
      <div className="bg-white stakeholder-summary-card rounded-lg p-6 shadow flex flex-col md:flex-row gap-4 items-center">
        <input
          type="text"
          placeholder="Search learners..."
          className="border rounded-md px-3 py-2 text-sm stakeholder-input w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <FilterSelect
          label="Coach"
          value={filterCoach}
          setValue={setFilterCoach}
          options={[
            ["all", "All"],
            ["has", "Has Coach"],
            ["none", "No Coach"],
          ]}
        />

        <FilterSelect
          label="AP"
          value={filterAp}
          setValue={setFilterAp}
          options={[
            ["all", "All"],
            ["has", "Has AP"],
            ["none", "No AP"],
          ]}
        />

        <FilterSelect
          label="Sort"
          value={sortBy}
          setValue={setSortBy}
          options={[
            ["name", "Sort by Name"],
            ["email", "Sort by Email"],
          ]}
        />

        <div className="text-sm text-muted md:ml-auto">
          Showing {filteredLearners.length} of {learners.length}
        </div>
      </div>

      {/* Learners Table */}
      <div className="bg-white stakeholder-summary-card rounded-lg p-6 shadow">
        <h2 className="text-title mb-4">Learners ({learners.length})</h2>

        {learners.length === 0 ? (
          <p className="text-muted italic">No learners enrolled yet.</p>
        ) : (
          <table className="min-w-full border-collapse text-[15px]">
            <thead className="bg-gray-50">
              <tr className="border-b">
                <Th>Learner</Th>
                <Th>Email</Th>
                <Th>Coach</Th>
                <Th>AP</Th>
                <Th>Cohort</Th>
              </tr>
            </thead>

            <tbody>
              {filteredLearners.map((l) => (
                <tr
                  key={l.user_id}
                  className="border-b hover:bg-gray-100 transition"
                >
                  <Td className="font-semibold">
                    {`${l.first_name} ${l.last_name}`.trim() || l.email}
                  </Td>

                  <Td>{l.email}</Td>

                  {/* Coach Select */}
                  <Td>
                    <select
                      className="border rounded-md px-2 py-1 text-sm stakeholder-dropdown"
                      value={l.coach_id || ""}
                      onChange={(e) => {
                        const supporterId = e.target.value || null;

                        adminService
                          .assignSupport(courseId, {
                            learner_id: l.user_id,
                            supporter_id: supporterId,
                            type: "coach",
                          })
                          .then(() => {
                            loadDetail();
                            toast.success("Coach updated.");
                          })
                          .catch(() => toast.error("Failed to update coach."));
                      }}
                      disabled={saving}
                    >
                      <option value="">None</option>
                      {coaches.map((c) => (
                        <option key={c.user_id} value={c.user_id}>
                          {`${c.first_name} ${c.last_name}`.trim() || c.email}
                        </option>
                      ))}
                    </select>
                  </Td>

                  {/* AP Select */}
                  <Td>
                    <select
                      className="border rounded-md px-2 py-1 text-sm stakeholder-dropdown"
                      value={l.ap_id || ""}
                      onChange={(e) => {
                        const supporterId = e.target.value || null;

                        adminService
                          .assignSupport(courseId, {
                            learner_id: l.user_id,
                            supporter_id: supporterId,
                            type: "accountability_partner",
                          })
                          .then(() => {
                            loadDetail();
                            toast.success("AP updated.");
                          })
                          .catch(() => toast.error("Failed to update AP."));
                      }}
                      disabled={saving}
                    >
                      <option value="">None</option>
                      {aps.map((a) => (
                        <option key={a.user_id} value={a.user_id}>
                          {`${a.first_name} ${a.last_name}`.trim() || a.email}
                        </option>
                      ))}
                    </select>
                  </Td>

                  {/* Cohort Select */}
                  <Td>
                    <select
                      className="border rounded-md px-2 py-1 text-sm stakeholder-dropdown"
                      value={cohortMap[l.user_id] || ""}
                      onChange={(e) =>
                        handleAssignCohort(l.user_id, e.target.value)
                      }
                      disabled={saving}
                    >
                      <option value="">None</option>
                      {cohorts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Coaches + AP summary */}
      <div className="grid md:grid-cols-2 gap-6">
        <SummaryCard title={`Coaches (${coaches.length})`}>
          {coaches.length === 0 ? (
            <p className="text-muted italic text-sm">No coaches.</p>
          ) : (
            <ul className="space-y-1 text-body">
              {coaches.map((c) => (
                <li key={c.user_id}>
                  {`${c.first_name} ${c.last_name}`.trim() || c.email}
                  <span className="text-muted text-xs ml-1">({c.email})</span>
                </li>
              ))}
            </ul>
          )}
        </SummaryCard>

        <SummaryCard title={`Accountability Partners (${aps.length})`}>
          {aps.length === 0 ? (
            <p className="text-muted italic text-sm">No APs.</p>
          ) : (
            <ul className="space-y-1 text-body">
              {aps.map((a) => (
                <li key={a.user_id}>
                  {`${a.first_name} ${a.last_name}`.trim() || a.email}
                  <span className="text-muted text-xs ml-1">({a.email})</span>
                </li>
              ))}
            </ul>
          )}
        </SummaryCard>
      </div>
    </div>
  );
};

export default CourseDetail;

/* ===========================================================
   Small Subcomponents for cleaner JSX
   =========================================================== */

const Th = ({ children }) => (
  <th className="p-2 text-left text-title font-semibold">{children}</th>
);

const Td = ({ children, className = "" }) => (
  <td className={`p-2 ${className}`}>{children}</td>
);

const FilterSelect = ({ value, setValue, options }) => (
  <select
    className="border p-2 rounded-md text-sm stakeholder-dropdown"
    value={value}
    onChange={(e) => setValue(e.target.value)}
  >
    {options.map(([val, label]) => (
      <option key={val} value={val}>
        {label}
      </option>
    ))}
  </select>
);

const SummaryCard = ({ title, children }) => (
  <div className="bg-white stakeholder-summary-card rounded-lg p-6 shadow">
    <h3 className="text-title mb-2">{title}</h3>
    {children}
  </div>
);

const StatCard = ({ label, value }) => (
  <div className="bg-white shadow rounded-lg p-5 text-center stakeholder-summary-card">
    <p className="text-muted text-sm">{label}</p>
    <p className="text-2xl font-semibold text-[#323232] mt-1">{value}</p>
  </div>
);
